import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { Container } from '@plato/ui/containers/container';
import { cn } from '@plato/ui/util/classes';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import type { ReactElement, ReactNode } from 'react';

export type AskPlatoSurfaceProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Forwarded to the popup — the panel points it at its composer. */
    initialFocus?: DialogPrimitive.Popup.Props['initialFocus'];
    children: ReactNode;
};

/**
 * The Ask Plato chat surface: on desktop a picker-style popover anchored
 * above the FAB in the bottom-right corner, on mobile a full-screen takeover.
 *
 * Desktop is deliberately non-modal — no focus trap, no scroll lock, and
 * outside presses don't dismiss it — so the user can keep scrolling a list or
 * open a customer while the chat stays put. The mobile takeover covers
 * everything, so there it is a proper modal dialog (focus trap, scroll lock).
 */
export function AskPlatoSurface({
    open,
    onOpenChange,
    initialFocus,
    children,
}: AskPlatoSurfaceProps): ReactElement {
    const isDesktop = useBreakpoint('sm');

    return (
        <DialogPrimitive.Root
            open={open}
            modal={!isDesktop}
            disablePointerDismissal
            onOpenChange={(next) => {
                onOpenChange(next);
            }}
        >
            <DialogPrimitive.Portal>
                <DialogPrimitive.Viewport
                    // `pointer-events-none` on the viewport keeps the app
                    // underneath clickable while the popover is open.
                    className={cn(
                        'pointer-events-none fixed inset-0 z-50 flex items-end justify-end',
                        // Desktop: clear the FAB (bottom-3 + size-13 + gap-cozy) and
                        // sit below it, so its shadow never lands on the FAB.
                        'sm:z-40 sm:pr-4 sm:pb-18',
                    )}
                >
                    <Container
                        level={1}
                        render={
                            <DialogPrimitive.Popup
                                data-slot="ask-plato-surface"
                                initialFocus={initialFocus}
                                className={cn(
                                    'motion-popup pointer-events-auto flex min-h-0 flex-col overflow-hidden',
                                    'bg-container text-content outline-none',
                                    // With no exit animation on mobile the primitive
                                    // keeps the popup mounted; hide it when closed.
                                    'data-closed:hidden',
                                    // Mobile: edge-to-edge takeover.
                                    'h-full w-full',
                                    // Desktop: the anchored popover panel.
                                    'sm:h-[min(44rem,calc(100dvh-6rem))] sm:w-md sm:max-w-[calc(100vw-2.5rem)]',
                                    'sm:rounded-relaxed sm:border sm:border-line-weak sm:shadow-2xl',
                                )}
                            />
                        }
                    >
                        {children}
                    </Container>
                </DialogPrimitive.Viewport>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
